import { BuildTreeNode, SharedTree, BuildNode } from '@fluid-experimental/tree';
import { logVerbose } from '../../log';
import { MfsItem, MfsItemId, MfsValue, MfsBase, MfsCollectionCommon, isMfsCollection } from '../..';
import { assertPrecondition, mfsError } from '../../errors';
import { getCollectionValues } from '../../store/collections';
import { isMfsValueArray } from '../../store/mfsValueTypeGuards';
import { COLLECTION_KIND_KEY, ID_KEY, isNullOrWhitespace, LABEL_PROPERTY } from '../../utility';

import { COLLECTION_VALUES, NodeKind, PROPERTY_VALUES } from './types';

/** Convert given `item` to `BuildTreeNode`. */
export function createMfsItemNode<T extends MfsItem>(item: T, tree: SharedTree): Required<BuildTreeNode> {
  const traits: PropertyDescriptorMap = {};

  let undefinedCount = 0;
  let immutableCount = 0;
  for (const [propertyKey, propertyValue] of Object.entries(item)) {
    if (propertyValue === undefined) {
      undefinedCount++;
      // skip inserting undefined values
      continue;
    }

    if (isImmutableProperty(propertyKey)) {
      // immutable properties will be saved in the payload.
      immutableCount++;
      continue;
    }

    traits[propertyKey] = {
      value: createMfsPropertyNode(propertyValue),
      enumerable: true,
    };
  }

  logVerbose('createMfsItemNode', `Skipped ${undefinedCount} undefined and ${immutableCount} immutable property keys`);

  // add elements traits, if the item is a collection.
  if (isMfsCollection(item)) {
    logVerbose('createMfsItemNode', 'Item is mfs collection');
    const collectionValues = getCollectionValues(item);

    traits[COLLECTION_VALUES] = {
      value: createCollectionElementNodes(collectionValues),
      enumerable: true,
    };
  }

  return {
    identifier: tree.generateNodeId(),
    definition: NodeKind.Item,
    traits: Object.defineProperties({}, traits),
    payload: { id: item.id, label: item.label, ...(item.kind && { kind: item.kind }) },
  };
}

/** Create the node or nodes that represents the given multi-or single value. */
export function createMfsPropertyNode<T extends MfsValue>(value: T): BuildNode[] {
  if (isMfsValueArray(value)) {
    return [createArrayNode(value)];
  }

  return [createLeafNode(value, NodeKind.SingleValueNode)];
}

/** Create a node that represents the given value. */
export function createCollectionElementNodes(values: MfsValue): readonly BuildNode[] {
  assertPrecondition(isValidCollectionValues(values), mfsError.invalidCollectionValues);
  return values.map((itemId) => createLeafNode(itemId, NodeKind.Reference));
}

function isValidCollectionValues(value: MfsValue): value is MfsItemId[] {
  return !!value && Array.isArray(value) && !value.some((v) => !isMfsItemId(v));
}

function isMfsItemId(v: MfsValue): v is MfsItemId {
  return !!v && typeof v === 'string' && !isNullOrWhitespace(v);
}

function isImmutableProperty(property: string): boolean {
  const propertyKey = property as keyof MfsBase | keyof MfsCollectionCommon;
  return propertyKey === ID_KEY || propertyKey === LABEL_PROPERTY || propertyKey === COLLECTION_KIND_KEY;
}

/** Create a leaf change node where all value is in payload. */
export const createLeafNode = (val: unknown, definition: NodeKind): BuildNode => ({
  definition: definition,
  payload: val,
});

/** Create a leaf change node where all value is in payload. */
export const createArrayNode = (values: MfsValue[]): BuildNode => ({
  definition: NodeKind.Array,
  // "Values" trait for multi-value properties
  traits: { [PROPERTY_VALUES]: values.map((value) => createLeafNode(value, NodeKind.MultiValueNode)) },
});
